import React from 'react';
import { Link } from 'react-router-dom';
import { AdminInterestAnalyticsDto, getAdminInterestAnalytics, useApiClient } from '../api/client';
import { useI18n } from '../i18n/I18nProvider';
import { MetricCard } from './ui';

type BarItem = { id: string; label: string; detail?: string; count: number };

const BarChart: React.FC<{ title: string; description: string; items: BarItem[]; totalUsers: number; color?: string }> = ({
  title, description, items, totalUsers, color = 'bg-secondary',
}) => {
  const { t, language } = useI18n();
  const number = new Intl.NumberFormat(language);
  const percent = new Intl.NumberFormat(language, { style: 'percent', maximumFractionDigits: 1 });
  const maximum = Math.max(1, ...items.map((item) => item.count));
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm" aria-label={title}>
      <h2 className="text-lg font-bold text-gray-900">{title}</h2>
      <p className="mb-5 mt-1 text-sm text-gray-500">{description}</p>
      {items.length === 0 ? <p className="py-8 text-center text-sm text-gray-500">{t('analytics.noData')}</p> : (
        <ul className="max-h-[36rem] space-y-4 overflow-y-auto pr-2" aria-label={title}>
          {items.map((item) => (
            <li key={item.id}>
              <div className="mb-1.5 flex items-start justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <span className="break-words font-semibold text-gray-800">{item.label}</span>
                  {item.detail && <div className="text-xs text-gray-500">{item.detail}</div>}
                </div>
                <div className="shrink-0 text-right tabular-nums">
                  <span className="font-bold text-gray-900">{number.format(item.count)}</span>
                  <span className="ml-2 text-xs text-gray-500">{totalUsers ? percent.format(item.count / totalUsers) : '—'}</span>
                </div>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
                <div className={`h-full rounded-full ${color}`} style={{ width: `${item.count / maximum * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export const InterestAnalyticsTab: React.FC = () => {
  const { request } = useApiClient();
  const { t, language } = useI18n();
  const [data, setData] = React.useState<AdminInterestAnalyticsDto | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [refresh, setRefresh] = React.useState(0);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getAdminInterestAnalytics(request)
      .then((result) => { if (active) setData(result); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [request, refresh]);

  const number = new Intl.NumberFormat(language);
  const decimal = new Intl.NumberFormat(language, { minimumFractionDigits: 1, maximumFractionDigits: 2 });
  const percent = new Intl.NumberFormat(language, { style: 'percent', maximumFractionDigits: 1 });
  const name = (item: { name: string; nameEn?: string | null; nameDe?: string | null }) =>
    (language === 'de' ? item.nameDe : item.nameEn)?.trim() || item.name;
  const metricValue = (value: string, hint: string) => (
    <><span>{value}</span><div className="mt-2 text-xs font-normal leading-5">{hint}</div></>
  );

  const summary = data?.summary;
  const categories = data?.categories.slice().sort((a, b) => b.userCount - a.userCount || name(a).localeCompare(name(b), language)) ?? [];
  const interests = data?.interests.slice().sort((a, b) => b.userCount - a.userCount || name(a).localeCompare(name(b), language)) ?? [];
  const categoryNames = new Map(categories.map((item) => [item.categoryId, name(item)]));
  const distribution = data?.distribution.slice() ?? [];
  if (data && !distribution.some((item) => item.interestCount === 0)) distribution.push({ interestCount: 0, userCount: 0 });
  distribution.sort((a, b) => a.interestCount - b.interestCount);

  return (
    <div className="space-y-6" role="tabpanel" aria-label={t('analytics.tab.interests')}>
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4">
        <div className="max-w-3xl text-sm leading-6 text-blue-900">
          <p className="font-semibold">{t('analytics.interests.snapshot')}</p>
          <p>{t('analytics.interests.scope')}</p>
          {!loading && !error && data && <p className="mt-1 text-xs text-blue-700">{t('analytics.interests.updated', { date: new Date(data.generatedAt).toLocaleString(language) })}</p>}
        </div>
        <div className="flex gap-2">
          <Link to="/interests" className="rounded-lg bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">{t('analytics.interests.manage')}</Link>
          <button type="button" className="rounded-lg bg-secondary px-3 py-2 text-sm font-semibold text-white disabled:opacity-50" onClick={() => setRefresh((value) => value + 1)} disabled={loading}>
            {t('action.refresh')}
          </button>
        </div>
      </div>
      {loading && <div className="rounded-xl border bg-white p-12 text-center text-gray-500" role="status">{t('analytics.loading')}</div>}
      {!loading && error && (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-5 text-red-700" role="alert">
          <span>{t('analytics.interests.error')}</span>
          <button type="button" className="rounded-lg bg-white px-3 py-2 text-sm font-semibold" onClick={() => setRefresh((value) => value + 1)}>{t('analytics.interests.retry')}</button>
        </div>
      )}
      {!loading && !error && summary && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard label={t('analytics.interests.with')} tone="green" value={metricValue(number.format(summary.usersWithInterests), t('analytics.interests.coverage', {
              rate: summary.totalUsers ? percent.format(summary.participationRate / 100) : '—', total: number.format(summary.totalUsers),
            }))} />
            <MetricCard label={t('analytics.interests.without')} tone="amber" value={metricValue(number.format(summary.usersWithoutInterests), t('analytics.interests.withoutHint'))} />
            <MetricCard label={t('analytics.interests.averageAll')} tone="blue" value={metricValue(summary.totalUsers ? decimal.format(summary.averagePerUser) : '—', t('analytics.interests.averageAllHint'))} />
            <MetricCard label={t('analytics.interests.averageParticipants')} tone="violet" value={metricValue(summary.usersWithInterests ? decimal.format(summary.averagePerParticipatingUser) : '—', t('analytics.interests.averageParticipantsHint'))} />
          </div>
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm" aria-label={t('analytics.interests.participation')}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-lg font-bold text-gray-900">{t('analytics.interests.participation')}</h2>
              <p className="text-sm text-gray-500">{t('analytics.interests.totalSelections', { count: number.format(summary.totalSelections) })}</p>
            </div>
            {summary.totalUsers === 0 ? <p className="mt-3 text-sm text-gray-500">{t('analytics.interests.noUsers')}</p> : (
              <>
                <div className="my-4 flex h-5 overflow-hidden rounded-full bg-amber-100" aria-hidden="true">
                  <div className="h-full bg-emerald-500" style={{ width: `${summary.usersWithInterests / summary.totalUsers * 100}%` }} />
                </div>
                <div className="flex flex-wrap justify-between gap-2 text-sm">
                  <span className="text-emerald-700">{t('analytics.interests.participatingCount', { count: number.format(summary.usersWithInterests) })}</span>
                  <span className="text-amber-800">{t('analytics.interests.missingCount', { count: number.format(summary.usersWithoutInterests) })}</span>
                </div>
              </>
            )}
          </section>
          <div className="grid items-start gap-6 xl:grid-cols-2">
            <BarChart title={t('analytics.interests.popularity')} description={t('analytics.interests.popularityHint')} totalUsers={summary.totalUsers}
              items={interests.map((item) => ({ id: item.interestId, label: name(item), detail: categoryNames.get(item.categoryId), count: item.userCount }))} />
            <div className="space-y-6">
              <BarChart title={t('analytics.interests.categories')} description={t('analytics.interests.categoriesHint')} totalUsers={summary.totalUsers} color="bg-violet-500"
                items={categories.map((item) => ({ id: item.categoryId, label: name(item), count: item.userCount }))} />
              <BarChart title={t('analytics.interests.distribution')} description={t('analytics.interests.distributionHint')} totalUsers={summary.totalUsers} color="bg-sky-500"
                items={summary.totalUsers === 0 ? [] : distribution.map((item) => ({ id: String(item.interestCount), label: t(item.interestCount === 1 ? 'analytics.interests.oneSelection' : 'analytics.interests.selectionCount', { count: item.interestCount }), count: item.userCount }))} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
